import { gql } from '@urql/core';

export const GET_CUSTOMERS = gql`
  query GetCustomers($options: CustomerListOptions) {
    customers(options: $options) {
      items {
        id
        firstName
        lastName
        emailAddress
        phoneNumber
        createdAt
        customFields {
          accountType
          companyName
        }
        orders(options: { filter: { orderPlacedAt: { isNull: false }, state: { notIn: ["AddingItems", "Draft", "ArrangingPayment"] } } }) {
          totalItems
        }
      }
      totalItems
    }
  }
`;

export const GET_CUSTOMER_DETAIL = gql`
  query GetCustomerDetail($id: ID!) {
    customer(id: $id) {
      id
      firstName
      lastName
      emailAddress
      phoneNumber
      createdAt
      updatedAt
      customFields {
        accountType
        companyName
        vatNumber
        commercialRegistration
      }
      user {
        id
        verified
        lastLogin
      }
      addresses {
        id
        fullName
        company
        streetLine1
        streetLine2
        city
        province
        postalCode
        country {
          code
          name
        }
        phoneNumber
        defaultShippingAddress
        defaultBillingAddress
      }
      orders(options: { take: 10, sort: { orderPlacedAt: DESC }, filter: { orderPlacedAt: { isNull: false }, state: { notIn: ["AddingItems", "Draft", "ArrangingPayment"] } } }) {
        items {
          id
          code
          state
          totalWithTax
          orderPlacedAt
        }
        totalItems
      }
      history(options: { take: 20, sort: { createdAt: DESC } }) {
        items {
          id
          type
          createdAt
          data
          administrator {
            firstName
            lastName
          }
        }
        totalItems
      }
    }
  }
`;

export const CREATE_CUSTOMER = gql`
  mutation CreateCustomer($input: CreateCustomerInput!, $password: String) {
    createCustomer(input: $input, password: $password) {
      ... on Customer {
        id
        firstName
        lastName
        emailAddress
        phoneNumber
        createdAt
      }
      ... on EmailAddressConflictError {
        errorCode
        message
      }
    }
  }
`;

export const UPDATE_CUSTOMER = gql`
  mutation UpdateCustomer($input: UpdateCustomerInput!) {
    updateCustomer(input: $input) {
      ... on Customer {
        id
        firstName
        lastName
        emailAddress
        phoneNumber
        customFields {
          accountType
          companyName
          vatNumber
          commercialRegistration
        }
      }
      ... on EmailAddressConflictError {
        errorCode
        message
      }
    }
  }
`;

export const DELETE_CUSTOMER = gql`
  mutation DeleteCustomer($id: ID!) {
    deleteCustomer(id: $id) {
      result
      message
    }
  }
`;

export const DELETE_CUSTOMERS = gql`
  mutation DeleteCustomers($ids: [ID!]!) {
    deleteCustomers(ids: $ids) {
      result
      message
    }
  }
`;

export const CREATE_CUSTOMER_ADDRESS = gql`
  mutation CreateCustomerAddress($customerId: ID!, $input: CreateAddressInput!) {
    createCustomerAddress(customerId: $customerId, input: $input) {
      id
      fullName
      company
      streetLine1
      streetLine2
      city
      province
      postalCode
      country {
        code
        name
      }
      phoneNumber
      defaultShippingAddress
      defaultBillingAddress
    }
  }
`;

export const UPDATE_CUSTOMER_ADDRESS = gql`
  mutation UpdateCustomerAddress($input: UpdateAddressInput!) {
    updateCustomerAddress(input: $input) {
      id
      fullName
      company
      streetLine1
      streetLine2
      city
      province
      postalCode
      country {
        code
        name
      }
      phoneNumber
      defaultShippingAddress
      defaultBillingAddress
    }
  }
`;

export const DELETE_CUSTOMER_ADDRESS = gql`
  mutation DeleteCustomerAddress($id: ID!) {
    deleteCustomerAddress(id: $id) {
      success
    }
  }
`;

export const ADD_CUSTOMER_NOTE = gql`
  mutation AddCustomerNote($input: AddNoteToCustomerInput!) {
    addNoteToCustomer(input: $input) {
      id
      history(options: { take: 1, sort: { createdAt: DESC } }) {
        items {
          id
          type
          createdAt
          data
          administrator {
            firstName
            lastName
          }
        }
      }
    }
  }
`;

export const DELETE_CUSTOMER_NOTE = gql`
  mutation DeleteCustomerNote($id: ID!) {
    deleteCustomerNote(id: $id) {
      result
      message
    }
  }
`;

export const GET_CUSTOMER_GROUPS = gql`
  query GetCustomerGroups($options: CustomerGroupListOptions) {
    customerGroups(options: $options) {
      items {
        id
        name
        customFields
        customers {
          totalItems
        }
      }
      totalItems
    }
  }
`;

export const GET_CUSTOMER_GROUP = gql`
  query GetCustomerGroup($id: ID!, $options: CustomerListOptions) {
    customerGroup(id: $id) {
      id
      name
      customFields
      customers(options: $options) {
        items {
          id
          firstName
          lastName
          emailAddress
        }
        totalItems
      }
    }
  }
`;

export const CREATE_CUSTOMER_GROUP = gql`
  mutation CreateCustomerGroup($input: CreateCustomerGroupInput!) {
    createCustomerGroup(input: $input) {
      id
      name
      customers {
        totalItems
      }
    }
  }
`;

export const UPDATE_CUSTOMER_GROUP = gql`
  mutation UpdateCustomerGroup($input: UpdateCustomerGroupInput!) {
    updateCustomerGroup(input: $input) {
      id
      name
    }
  }
`;

export const DELETE_CUSTOMER_GROUP = gql`
  mutation DeleteCustomerGroup($id: ID!) {
    deleteCustomerGroup(id: $id) {
      result
      message
    }
  }
`;

export const ADD_CUSTOMERS_TO_GROUP = gql`
  mutation AddCustomersToGroup($groupId: ID!, $customerIds: [ID!]!) {
    addCustomersToGroup(customerGroupId: $groupId, customerIds: $customerIds) {
      id
      name
      customers {
        totalItems
      }
    }
  }
`;

export const REMOVE_CUSTOMERS_FROM_GROUP = gql`
  mutation RemoveCustomersFromGroup($groupId: ID!, $customerIds: [ID!]!) {
    removeCustomersFromGroup(customerGroupId: $groupId, customerIds: $customerIds) {
      id
      name
      customers {
        totalItems
      }
    }
  }
`;

export const GET_SEGMENT_CONDITION_DEFINITIONS = gql`
  query GetSegmentConditionDefinitions {
    segmentConditionDefinitions {
      code
      description {
        languageCode
        value
      }
      category
      args {
        name
        type
        label {
          languageCode
          value
        }
        required
        defaultValue
        ui
      }
    }
  }
`;

export const PREVIEW_SMART_SEGMENT = gql`
  query PreviewSmartSegment($rules: JSON!, $rootConnector: String!) {
    previewSmartSegment(rules: $rules, rootConnector: $rootConnector) {
      totalCount
      sampleCustomers {
        id
        firstName
        lastName
        emailAddress
      }
    }
  }
`;

export const CREATE_SMART_SEGMENT = gql`
  mutation CreateSmartSegment($input: CreateSmartSegmentInput!) {
    createSmartSegment(input: $input) {
      id
      customerGroup {
        id
        name
      }
      rules
      rootConnector
      memberCount
      autoUpdate
      status
      createdAt
    }
  }
`;

export const UPDATE_SMART_SEGMENT = gql`
  mutation UpdateSmartSegment($input: UpdateSmartSegmentInput!) {
    updateSmartSegment(input: $input) {
      id
      customerGroup {
        id
        name
      }
      rules
      rootConnector
      memberCount
      autoUpdate
      status
      updatedAt
    }
  }
`;

export const DELETE_SMART_SEGMENT = gql`
  mutation DeleteSmartSegment($id: ID!) {
    deleteSmartSegment(id: $id) {
      result
      message
    }
  }
`;

export const RECALCULATE_SMART_SEGMENT = gql`
  mutation RecalculateSmartSegment($id: ID!) {
    recalculateSmartSegment(id: $id) {
      id
      memberCount
      lastCalculatedAt
      status
    }
  }
`;

export const ADD_SMART_SEGMENT_OVERRIDE = gql`
  mutation AddSmartSegmentOverride($input: AddSmartSegmentOverrideInput!) {
    addSmartSegmentOverride(input: $input) {
      id
      overrides {
        id
        customerId
        customer {
          id
          firstName
          lastName
          emailAddress
        }
        overrideType
        reason
        createdAt
      }
    }
  }
`;

export const REMOVE_SMART_SEGMENT_OVERRIDE = gql`
  mutation RemoveSmartSegmentOverride($overrideId: ID!) {
    removeSmartSegmentOverride(overrideId: $overrideId) {
      id
      overrides {
        id
        customerId
        customer {
          id
          firstName
          lastName
          emailAddress
        }
        overrideType
        reason
        createdAt
      }
    }
  }
`;

export const GET_SMART_SEGMENT_BY_GROUP_ID = gql`
  query GetSmartSegmentByGroupId($groupId: ID!) {
    smartSegmentByGroupId(groupId: $groupId) {
      id
      rules
      rootConnector
      lastCalculatedAt
      memberCount
      autoUpdate
      status
      overrides {
        id
        customerId
        customer {
          id
          firstName
          lastName
          emailAddress
        }
        overrideType
        reason
        createdAt
      }
      createdAt
      updatedAt
    }
  }
`;

export const GET_SMART_SEGMENTS_LIGHT = gql`
  query GetSmartSegmentsLight {
    smartSegments(options: { take: 500 }) {
      items {
        id
        customerGroup {
          id
        }
        memberCount
        status
      }
      totalItems
    }
  }
`;

export const GET_RFM_DISTRIBUTION = gql`
  query GetRfmDistribution {
    rfmDistribution {
      totalCustomers
      hasEnoughData
      lastCalculatedAt
      groups {
        group
        label
        count
        percentage
        color
      }
    }
  }
`;

export const GET_CUSTOMER_RFM_SCORE = gql`
  query GetCustomerRfmScore($customerId: ID!) {
    customerRfmScore(customerId: $customerId) {
      recencyScore
      frequencyScore
      monetaryScore
      fmScore
      rfmGroup
      rfmGroupLabel
      daysSinceLastOrder
      totalOrders
      totalSpent
      calculatedAt
    }
  }
`;

export const RECALCULATE_RFM_SCORES = gql`
  mutation RecalculateRfmScores {
    recalculateRfmScores {
      success
      updated
      message
    }
  }
`;

export const GET_BACK_IN_STOCK_REPORT = gql`
  query GetBackInStockReport($options: BackInStockReportOptions) {
    backInStockReport(options: $options) {
      items {
        productVariantId
        productVariantName
        productName
        productId
        sku
        imageUrl
        stockOnHand
        sellableStock
        inventoryTracked
        subscriberCount
      }
      totalItems
    }
  }
`;
